import React, { useEffect, useState } from "react";

import ChipPopoverTrigger from "@/components/ChipPopoverTrigger";
import { FormCheckbox } from "@/components/forms/FormCheckbox";
import { FormRadio, FormRadioGroup } from "@/components/forms/FormRadio";
import { Overline } from "@/components/typography";
import { Popover, PopoverContent } from "@/components/ui/popover";

interface ChipSelectProps {
  active?: boolean;
  label: React.ReactNode;
  names?: string[] | React.ReactElement[];
  namesPrefix?: string;
  onChange: (newFilter: string[] | string) => unknown;
  options: string[];
  value: string[] | string;
  singleSelect?: boolean;
  disabled?: boolean;
}

function ChipSelect(props: ChipSelectProps) {
  const [open, setOpen] = useState(false);
  const { active, label, names, namesPrefix, onChange, options, singleSelect, value, disabled } =
    props;
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Commit filter synchronously so UI and filtered results stay in lockstep.
  const handleRemoveAll = () => {
    setLocalValue([]);
    onChange([]);
  };

  const handleOptionToggle = (option: string, checked: boolean) => {
    if (singleSelect) {
      setLocalValue(option);
      onChange(option);
      return;
    }

    const selected = new Set(localValue as string[]);
    if (checked) selected.add(option);
    else selected.delete(option);
    const next = Array.from(selected);
    setLocalValue(next);
    onChange(next);
  };

  const selectedRadioValue =
    typeof localValue === "string"
      ? localValue
      : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <ChipPopoverTrigger
        active={active}
        clearable={!singleSelect}
        disabled={disabled}
        onClear={handleRemoveAll}
      >
        {label}
      </ChipPopoverTrigger>
      <PopoverContent
        align="center"
        side="bottom"
        className="flex w-auto max-w-[min(24rem,calc(100vw-2rem))] max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0"
      >
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
          {namesPrefix ? (
              <Overline className="mb-3">
                {namesPrefix}
              </Overline>
          ) : null}

          {singleSelect ? (
            <FormRadioGroup
              value={selectedRadioValue || null}
              onChange={(next) => {
                if (next) handleOptionToggle(next, true);
              }}
              disabled={disabled}
              fieldClassName="gap-0"
            >
              {options.map((option, i) => (
                <FormRadio
                  key={option}
                  value={option}
                  label={names ? names[i] : option}
                  disabled={disabled}
                />
              ))}
            </FormRadioGroup>
          ) : (
            <div className="flex flex-col gap-3">
              {options.map((option, i) => (
                <FormCheckbox
                  key={option}
                  label={names ? names[i] : option}
                  checked={(localValue as string[]).includes(option)}
                  disabled={disabled}
                  onChange={(checked) => handleOptionToggle(option, checked)}
                />
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default ChipSelect;
